import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Music, ChevronLeft } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { useTabStore } from '../../stores/tabStore.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

export interface LastOpenedTab {
  tabId: string;
  title: string;
  artist?: string;
  loopRange?: string;
  speed?: number;
  trackId?: string;
  currentMeasure?: number;
  totalMeasures?: number;
  progressPercent?: number;
}

export interface ContinueCardProps {
  tabData: LastOpenedTab | null | undefined;
}

export const ContinueCard: React.FC<ContinueCardProps> = ({ tabData }) => {
  const navigate = useNavigate();

  if (!tabData || !tabData.tabId || !tabData.title) {
    return null;
  }

  const handleContinue = () => {
    if (tabData.speed) {
      useTabStore.getState().setPlaybackSpeed(tabData.speed / 100);
    }
    if (tabData.loopRange) {
      const parts = tabData.loopRange.split(/[–-]/).map((s) => parseInt(s.trim(), 10));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        useTabStore.getState().setRestoreForTabId(tabData.tabId);
        useTabStore.getState().setLooping(true);
        useTabStore.getState().setLoopRange(parts[0], parts[1]);
      }
    }
    navigate(`/tabs/${tabData.tabId}`);
  };

  // FND-13: unknown values are hidden, never fabricated.
  const currentMeasure = tabData.currentMeasure;
  const totalMeasures = tabData.totalMeasures;
  const hasBars =
    typeof currentMeasure === 'number' && typeof totalMeasures === 'number' && totalMeasures > 1;
  const progressPercent = hasBars
    ? (tabData.progressPercent ??
      Math.min(100, Math.round((currentMeasure / Math.max(1, totalMeasures)) * 100)))
    : null;

  return (
    <section
      aria-label="ادامه تمرین"
      data-testid="continue-card"
      className="bg-base-200 border border-base-300 rounded-2xl md:rounded-[20px] p-3.5 md:p-5"
    >
      {/* Mobile view (< md) */}
      <div
        className="flex md:hidden items-center gap-3 cursor-pointer"
        onClick={handleContinue}
      >
        <span className="size-12 shrink-0 rounded-xl bg-neutral text-primary-soft flex items-center justify-center">
          <Music className="size-5" />
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-[11px] font-semibold text-primary-soft">ادامه تمرین</span>
          <span
            className="text-[15px] font-semibold text-base-content truncate"
            dir="ltr"
            data-testid="continue-card-title"
          >
            {tabData.title}
          </span>
          <span className="text-xs text-base-content/60 truncate">
            {tabData.loopRange ? 'لوپ ' : ''}
            <span dir="ltr" className="font-mono">
              {tabData.loopRange || ''}
              {tabData.loopRange && tabData.speed ? ' · ' : ''}
              {tabData.speed ? `${tabData.speed}%` : ''}
            </span>
          </span>
        </div>
        <button
          type="button"
          aria-label="ادامه"
          data-testid="continue-card-cta"
          className="size-8 rounded-lg flex items-center justify-center text-base-content/60 hover:text-base-content shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            handleContinue();
          }}
        >
          <ChevronLeft className="size-5" />
        </button>
      </div>

      {/* Desktop view (>= md) */}
      <div className="hidden md:flex items-center gap-5 w-full">
        {/* Mini notation preview box (220x96 LTR) */}
        <div
          dir="ltr"
          className="w-[220px] h-[96px] shrink-0 rounded-[14px] bg-base-100 border border-base-300 p-3 flex flex-col justify-between"
        >
          <div className="flex flex-col gap-2">
            <div className="h-px bg-slate-600/50" />
            <div className="h-px bg-slate-600/50" />
            <div className="h-px bg-slate-600/50" />
            <div className="h-px bg-slate-600/50" />
            <div className="h-px bg-slate-600/50" />
          </div>
          {hasBars && (
            <div className="flex gap-2 items-center" data-testid="continue-card-bars">
              <div className="h-1 flex-1 rounded-full bg-base-300 overflow-hidden">
                <div
                  className="h-1 bg-primary rounded-full"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="font-mono text-[10px] text-base-content/60 shrink-0">
                میزان {toPersianDigits(currentMeasure ?? 0)} از {toPersianDigits(totalMeasures ?? 0)}
              </span>
            </div>
          )}
        </div>

        {/* Tab title & badges */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-primary-soft">ادامه از جایی که ماندید</p>
          <p
            className="text-lg font-bold mt-1 text-base-content truncate"
            dir="ltr"
            data-testid="continue-card-title"
          >
            {tabData.title}
            {tabData.artist && (
              <span className="font-medium text-base-content/60"> · {tabData.artist}</span>
            )}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {tabData.loopRange && (
              <span className="h-6 px-2.5 rounded-full bg-secondary/12 text-secondary-soft text-xs font-semibold inline-flex items-center gap-1">
                لوپ <span dir="ltr" className="font-mono">{tabData.loopRange}</span>
              </span>
            )}
            {(tabData.speed || tabData.trackId) && (
              <span
                dir="ltr"
                className="h-6 px-2.5 rounded-full bg-neutral border border-base-300 text-neutral-content/80 font-mono text-[11px] inline-flex items-center"
              >
                {tabData.speed ? `${tabData.speed}%` : ''}
                {tabData.speed && tabData.trackId ? ' · ' : ''}
                {tabData.trackId || ''}
              </span>
            )}
          </div>
        </div>

        {/* Continue button */}
        <Button
          variant="primary"
          size="md"
          leadingIcon={<Play className="size-4 fill-current" />}
          onClick={handleContinue}
          data-testid="continue-card-cta"
        >
          ادامه
        </Button>
      </div>
    </section>
  );
};

ContinueCard.displayName = 'ContinueCard';
